import { useState } from "react";
import AuthFlowLayout from "../../layouts/AuthFlowLayout";
import TextInput from "../../components/TextInput";
import GradientButton from "../../components/GradientButton";
import SelectInput from "../../components/SelectInput";
import { UserRole } from "@quantizr-engineering/common/types/user";
import { enumToOptions } from "../../utils/enumToOptions";
import EmailInput from "../../components/EmailInput";
import { useAuthService } from "../../api/authService";
import { useNavigate } from "react-router-dom";
import PrimaryColorButton from "../../components/PrimaryColorButton";
import PasswordInput from "../../components/PasswordInput";
import ButtonContainer from "../../components/ButtonContainer";
import IconLink from "../../components/IconLink";
import { useFormik } from "formik";
import * as Yup from "yup";
import Loader from "../../components/Loader";
import { toast } from "sonner";

const Signup = () => {
  const [stage, setStage] = useState(1);
  const { register, isRegisterLoading } = useAuthService();

  const userRoleOptions = enumToOptions(UserRole);

  const navigate = useNavigate();

  const validationSchema = Yup.object().shape({
    firstName: Yup.string().required("First name is required"),
    lastName: Yup.string().required("Last name is required"),
    email: Yup.string()
      .email("Invalid email format")
      .required("Email is required"),
    password: Yup.string()
      .required("Password is required")
      .min(8, "Password must be at least 8 characters")
      .matches(/[0-9]/, "Password must contain at least one number")
      .matches(/[A-Z]/, "Password must contain at least one uppercase letter")
      .matches(/[a-z]/, "Password must contain at least one lowercase letter")
      .matches(
        /[!@#$%^&*(),.?":{}|<>]/,
        "Password must contain at least one symbol"
      ),
    teamName: Yup.string().required("Company name is required"),
    userRole: Yup.array()
      .of(Yup.string().required("User role is required"))
      .min(1, "One user role is required")
      .required("User role is required"),
    termsOfServiceAccepted: Yup.boolean().oneOf(
      [true],
      "You must accept the terms of use"
    ),
    amAuthorized: Yup.boolean().oneOf([true], "You must be authorized"),
  });

  const initialValues = {
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    teamName: "",
    userRole: [] as UserRole[],
    termsOfServiceAccepted: false,
    amAuthorized: false,
  };

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: (values) => {
      const userInfo = {
        ...values,
      };

      register(userInfo)
        .then(() => {
          navigate("/runs");
        })
        .catch((error) => {
          toast.error("Incorrect username or password");
        });
    },
  });

  const handleNextStage = () => {
    const stage1Fields = [
      "firstName",
      "lastName",
      "email",
      "password",
      "teamName",
      "userRole",
      "amAuthorized",
      "termsOfServiceAccepted",
    ];
    const hasErrors = stage1Fields.some(
      (field) => formik.errors[field as keyof typeof formik.errors]
    );
    const allFieldsTouched = stage1Fields.every(
      (field) => formik.touched[field as keyof typeof formik.touched]
    );

    if (!hasErrors && allFieldsTouched) {
      setStage(2);
    } else {
      stage1Fields.forEach((field) => formik.setFieldTouched(field, true));
    }
  };

  return (
    <AuthFlowLayout
      headerText={stage === 1 ? "Create your account" : "Invite Team Members"}
      headerContent={
        <IconLink to="/" src="/Logo-light.svg" alt="Logo" className="mb-4" />
      }
      isLoading={isRegisterLoading}
      loader={<Loader />}
    >
      <form onSubmit={formik.handleSubmit}>
        {stage === 1 && (
          <>
            <TextInput
              {...formik.getFieldProps("firstName")}
              label="First Name"
              error={formik.touched.firstName && formik.errors.firstName}
            />
            <TextInput
              {...formik.getFieldProps("lastName")}
              label="Last Name"
              error={formik.touched.lastName && formik.errors.lastName}
            />
            <EmailInput
              {...formik.getFieldProps("email")}
              label="Email"
              error={formik.touched.email && formik.errors.email}
            />
            <PasswordInput
              {...formik.getFieldProps("password")}
              label="Password"
              error={formik.touched.password && formik.errors.password}
            />
            <TextInput
              {...formik.getFieldProps("teamName")}
              label="Company Name"
              error={formik.touched.teamName && formik.errors.teamName}
            />
            <SelectInput
              {...formik.getFieldProps("userRole")}
              label="I am a..."
              options={userRoleOptions}
              value={formik.values.userRole}
              placeholder="Please select"
              onChange={(e) => {
                const selectedOption = e.target.value;
                formik.setFieldValue("userRole", [selectedOption]);
              }}
              error={formik.touched.userRole && formik.errors.userRole}
            />
            <div className="flex items-center my-2">
              <input
                type="checkbox"
                {...formik.getFieldProps("amAuthorized")}
                checked={formik.values.amAuthorized}
                className="checkbox border-gray-300 [--chkbg:theme(colors.custom-dark-purple)] [--chkfg:white] checked:border-transparent mr-2"
              />
              <span>
                I am authorized to sign on behalf of{" "}
                <b>{formik.values.teamName}</b>
              </span>
            </div>{" "}
            <div className="flex items-center my-2">
              <input
                type="checkbox"
                {...formik.getFieldProps("termsOfServiceAccepted")}
                checked={formik.values.termsOfServiceAccepted}
                className="checkbox border-gray-300 [--chkbg:theme(colors.custom-dark-purple)] [--chkfg:white] checked:border-transparent mr-2"
              />
              <span>
                I agree to the{" "}
                <a
                  className="text-blue-500"
                  href={`/terms-of-use`}
                  target="_blank"
                >
                  Terms Of Use
                </a>
              </span>
            </div>
            {formik.touched.termsOfServiceAccepted &&
              formik.errors.termsOfServiceAccepted && (
                <div className="text-red-500 text-xs mt-1">
                  {formik.errors.termsOfServiceAccepted}
                </div>
              )}
            <ButtonContainer
              firstButton={
                <PrimaryColorButton
                  type="button"
                  text="Back"
                  onClick={() => {
                    navigate("/login");
                  }}
                />
              }
              secondButton={
                <GradientButton
                  type="submit"
                  text="Submit"
                  disabled={isRegisterLoading}
                />
              }
            />
          </>
        )}
      </form>
    </AuthFlowLayout>
  );
};
export default Signup;
